import { useState, useEffect } from 'react';
import { clubsService, dashboardService, eventsService, friendsService } from '../../../../services';

const parseEventDate = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedValue)) {
            const [day, month, year] = trimmedValue.split('/').map(Number);
            const localDate = new Date(year, month - 1, day);
            return Number.isNaN(localDate.getTime()) ? null : localDate;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
            const [year, month, day] = trimmedValue.split('-').map(Number);
            const localDate = new Date(year, month - 1, day);
            return Number.isNaN(localDate.getTime()) ? null : localDate;
        }
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const useDashboardData = () => {
    const [data, setData] = useState({
        totalFriends: 0,
        totalClubs: 0,
        totalShoots: 0,
        totalEquipment: 0,
        recentActivity: [],
        upcomingEvents: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const endpoints = [
                    { name: 'friends', request: () => friendsService.getDashboardCount() },
                    { name: 'clubs', request: () => clubsService.getFavoriteClubs() },
                    { name: 'events', request: () => eventsService.getDashboardEvents() },
                    { name: 'equipment', request: () => dashboardService.getEquipmentCount() }
                ];

                const responses = await Promise.all(endpoints.map(async endpoint => {
                    try {
                        const data = await endpoint.request();
                        return { name: endpoint.name, data };
                    } catch (err) {
                        console.error(`Error fetching ${endpoint.name}:`, err);
                        throw err;
                    }
                }));

                const responseMap = Object.fromEntries(responses.map(r => [r.name, r.data]));

                setData({
                    totalFriends: responseMap.friends || 0,
                    totalClubs: responseMap.clubs.length || 0,
                    totalShoots: responseMap.events.length || 0,
                    totalEquipment: responseMap.equipment || 0,
                    recentActivity: responseMap.events?.map(event => ({
                        eventId: event.eventId,
                        name: event.name,
                        eventDate: event.eventDate,
                        eventType: event.eventType ?? event.EventType,
                        clubId: event.clubId,
                        clubName: event.clubName,
                        ownerUserId: event.ownerUserId,
                        ownerName: event.ownerName,
                        isOwner: Boolean(event.isOwner),
                        isShooter: Boolean(event.isShooter),
                        imageCount: event.imageCount || 0,
                        coverImageUrl: event.coverImageUrl || null,
                        shooters: event.shooters?.map(shooter => ({
                            eventShooterId: shooter.eventShooterId,
                            appUserId: shooter.appUserId,
                            name: shooter.name,
                            imageUrl: shooter.imageUrl,
                            equipment: shooter.equipment
                        })) || []
                    })) || [],
                    upcomingEvents: (responseMap.events || [])
                        .filter(event => {
                            const parsedDate = parseEventDate(event.eventDate);
                            return parsedDate && parsedDate > new Date();
                        })
                        .slice(0, 4)
                        .map(event => ({
                            eventId: event.eventId,
                            name: event.name,
                            date: event.eventDate,
                            location: event.clubName,
                            imageCount: event.imageCount || 0,
                            coverImageUrl: event.coverImageUrl || null,
                            eventType: event.eventType ?? event.EventType
                        }))
                });
            } catch (error) {
                console.error('Dashboard data fetch error:', error);
                setError(error.message || 'Failed to load dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    return { data, loading, error };
};
